import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// All forum links route through /forum?path=... so they open inside the
// iframe wrapper that keeps the SSi header persistent above Discourse.
const forumHref = (path) => `/forum?path=${encodeURIComponent(path || '/')}`;

/* ---------- Hero ---------- */

function CommunityHero() {
  return (
    <section style={cStyles.heroWrap} data-screen-label="Hero">
      <div style={cStyles.heroInner}>
        <div style={cStyles.heroKicker}>Our community</div>
        <h1 style={cStyles.heroH1}>
          Conversations are <em style={cStyles.heroH1Em}>why</em> we learn.
        </h1>
        <p style={cStyles.heroLede}>
          Behind all the SaySomethingin courses there's a forum that's been running for over 15 years.
          It's where learners ask, complain, organise meetups, post videos of themselves
          stumbling through their first 50 words, and welcome the next person to do the same.
          It's where Aran posted every day of a 75-day live experiment, and where the
          two-thousand-seven-hundredth reply to a thread about Oxford meetups just went up.
        </p>
        <p style={cStyles.heroLede}>
          You don't need a subscription to join. It's free, it's open, and we'd be delighted
          to see you there.
        </p>

        <div style={cStyles.heroActions}>
          <Link to={forumHref('/')} className="btn btn--primary">Visit the forum</Link>
          <a href="#what" style={cStyles.heroSecondary}>What's inside →</a>
        </div>

        <div style={cStyles.heroStats} className="c-hero-stats">
          <div style={cStyles.heroStat}>
            <div style={cStyles.heroStatN}>14+</div>
            <div style={cStyles.heroStatL}>years of conversation</div>
          </div>
          <div style={cStyles.heroStat}>
            <div style={cStyles.heroStatN}>280k</div>
            <div style={cStyles.heroStatL}>views on a single thread</div>
          </div>
          <div style={cStyles.heroStat}>
            <div style={cStyles.heroStatN}>40</div>
            <div style={cStyles.heroStatL}>languages so far</div>
          </div>
          <div style={cStyles.heroStat}>
            <div style={cStyles.heroStatN}>Daily</div>
            <div style={cStyles.heroStatL}>posts, including Aran's</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- The four corners ---------- */

const CORNERS = [
  {
    n: '01',
    title: 'Questions, encouragement, the lot',
    body: "Beginner's nerves, grammar arguments, why-isn't-this-working frustration. The thread “Tiny questions with quick answers” has 9,700+ replies, and “Why Welsh? What's your story?” is where almost everyone introduces themselves.",
    cta: 'Open general discussion',
    href: forumHref('/c/welsh/general-questions/11'),
  },
  {
    n: '02',
    title: 'Live experiments, in public',
    body: "Aran's just finished 75 days of Croatian, livestreamed to the forum. Tom did 30 days of Welsh. Aran, Tom and Kai did 10 days of Japanese and 10 days of Irish last year. Each one is documented in the forum so you can follow along, ask questions, watch the wobbles.",
    cta: 'See live experiments',
    href: forumHref('/t/75-days-of-croatian-livestreaming/42539'),
  },
  {
    n: '03',
    title: 'Meetups & real-world events',
    body: 'Eisteddfod lift-sharing, the Oxford Welsh learners group (running since 2014), the Saturday Zoom at noon UK time, CACEN in the South East, the touring London meet-up. The forum is where they all get organised.',
    cta: 'Find a meetup',
    href: forumHref('/c/welsh/meetups-events/12'),
  },
  {
    n: '04',
    title: 'Every language has its corner',
    body: 'Welsh has the biggest community by far, but Spanish, French, Italian, Korean, Japanese, Portuguese, Arabic, Mandarin, German and Irish all have their own threads, course feedback, and learners helping each other.',
    cta: 'Browse by language',
    href: forumHref('/c/other-languages/14'),
  },
];

function CommunityCorners() {
  return (
    <section style={cStyles.cornersWrap} id="what" data-screen-label="What's inside">
      <div style={cStyles.cornersInner}>
        <div style={cStyles.sectKicker}>What's inside</div>
        <h2 style={cStyles.sectH2}>
          Four corners of the forum.
        </h2>
        <p style={cStyles.cornersLede}>
          It's a Discourse install, which means it works like a slightly old-fashioned forum –
          on purpose. Threads breathe. Conversations come back to life years later. Here's the
          shape of it:
        </p>

        <div style={cStyles.cornersGrid} className="c-corners-grid">
          {CORNERS.map(c => (
            <Link key={c.n} to={c.href} style={cStyles.cornerCard} className="c-corner-card">
              <div style={cStyles.cornerN}>{c.n}</div>
              <h3 style={cStyles.cornerH3}>{c.title}</h3>
              <p style={cStyles.cornerP}>{c.body}</p>
              <span style={cStyles.cornerCta}>{c.cta} →</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Live from the forum ---------- */

// Seeded real threads scraped from the live forum on 27 May 2026 – used as a
// fallback when the /latest.json fetch is blocked by CORS in the browser. Once
// Discourse admin enables CORS for saysomethingin.com, the live fetch in
// CommunityLive replaces this. Re-scrape or hand-curate if the live fetch is
// still blocked in 6+ months and the seed has drifted.
const SEED_LATEST = [
  {
    title: '75 days of Croatian – livestreaming',
    href: forumHref('/t/75-days-of-croatian-livestreaming/42539'),
    category: 'General / Questions',
    posters: ['Aran', 'Michael', 'Martin'],
    replies: 38, activity: 'Today',
  },
  {
    title: 'What language(s) would you like us to build next?',
    href: forumHref('/t/what-language-s-would-you-like-us-to-build-next/42558'),
    category: 'Other Languages',
    posters: ['Aran', 'Garys', 'Richard', 'Meredith'],
    replies: 105, activity: 'Today',
  },
  {
    title: 'Why Welsh? Why SSiW? What’s your story?',
    href: forumHref('/t/why-welsh-why-ssiw-whats-your-story/36184'),
    category: 'General / Questions',
    posters: ['Catrin', 'Huw', 'Deborah', 'Siaron'],
    replies: 272, activity: 'May 24',
  },
  {
    title: 'Eisteddfod Genedlaethol 1–8 August 2026 – Sir Benfro',
    href: forumHref('/t/eisteddfod-genedlaethol-1-8-august-2026-sir-benfro/41198'),
    category: 'Meetups / Events',
    posters: ['Deborah'],
    replies: 14, activity: 'Yesterday',
  },
  {
    title: 'Anyone willing to have back and forth voice memo conversations on WhatsApp?',
    href: forumHref('/t/anyone-willing-to-have-back-and-forth-voice-memo-conversations-on-whatsapp/42608'),
    category: 'Meetups / Events',
    posters: ['Lukas', 'Deborah', 'Welshspringbok', 'Aran'],
    replies: 4, activity: 'Today',
  },
  {
    title: 'Bug reports for the new app (Tom’s PWA)',
    href: forumHref('/t/bug-reports-for-the-new-app-toms-pwa/42492'),
    category: 'General / Questions',
    posters: ['Aran', 'Richard', 'SteakAndEggs', 'Martin'],
    replies: 78, activity: 'Yesterday',
  },
];

function avatarHue(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return h % 360;
}

function Avatar({ name }) {
  const initial = name.slice(0, 1).toUpperCase();
  const hue = avatarHue(name);
  return (
    <span
      title={name}
      style={{
        ...cStyles.avatar,
        background: `hsl(${hue} 55% 88%)`,
        color: `hsl(${hue} 45% 28%)`,
      }}>{initial}</span>
  );
}

function ThreadCard({ t }) {
  return (
    <Link to={t.href} style={cStyles.threadCard} className="c-thread-card">
      <div style={cStyles.threadTop}>
        <span style={cStyles.threadCat}>{t.category}</span>
        <span style={cStyles.threadActivity}>{t.activity}</span>
      </div>
      <h3 style={cStyles.threadTitle}>{t.title}</h3>
      <div style={cStyles.threadFoot}>
        <div style={cStyles.posters}>
          {t.posters.slice(0, 4).map((p, i) => (
            <span key={`${p}-${i}`} style={{...cStyles.posterSlot, marginLeft: i === 0 ? 0 : -8}}>
              <Avatar name={p}/>
            </span>
          ))}
        </div>
        <span style={cStyles.replies}>
          <strong>{t.replies}</strong> replies
        </span>
      </div>
    </Link>
  );
}

function CommunityLive() {
  const [threads, setThreads] = useState(SEED_LATEST);
  const [live, setLive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('https://en.forum.saysomethingin.com/latest.json', {
          headers: { 'Accept': 'application/json' },
        });
        if (!res.ok) return;
        const json = await res.json();
        if (cancelled || !json?.topic_list?.topics) return;
        const cats = Object.fromEntries((json.topic_list.categories || []).map(c => [c.id, c.name]));
        const userById = Object.fromEntries((json.users || []).map(u => [u.id, u.username]));
        const mapped = json.topic_list.topics.slice(0, 6).map(top => {
          const posters = (top.posters || []).slice(0, 4)
            .map(p => userById[p.user_id])
            .filter(Boolean);
          return {
            title: top.title,
            href: forumHref(`/t/${top.slug}/${top.id}`),
            category: cats[top.category_id] || 'Forum',
            posters,
            replies: top.posts_count - 1,
            activity: new Date(top.last_posted_at).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' }),
          };
        });
        setThreads(mapped);
        setLive(true);
      } catch {
        /* CORS or network – keep seeded threads. */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section style={cStyles.liveWrap} data-screen-label="Live from the forum">
      <div style={cStyles.liveInner}>
        <div style={cStyles.liveHead}>
          <div>
            <div style={{...cStyles.sectKicker, color:'rgba(255,255,255,.85)'}}>
              <span style={cStyles.liveDot}/> {live ? 'Live right now' : 'Recent conversations'}
            </div>
            <h2 style={{...cStyles.sectH2, color:'#fff', maxWidth:'18ch'}}>
              What's on the forum this week.
            </h2>
          </div>
          <Link to={forumHref('/')} style={cStyles.liveAll}>
            All discussions →
          </Link>
        </div>

        <div style={cStyles.threadGrid} className="c-thread-grid">
          {threads.map((t, i) => <ThreadCard key={i} t={t}/>)}
        </div>
      </div>
    </section>
  );
}

/* ---------- Meetups + Slack ---------- */

const MEETUPS = [
  {
    when: 'Tuesdays',
    title: 'Oxford Welsh learners group',
    body: 'Running on the forum since 2014. 2,699 replies and counting – the longest-burning meetup thread on the site.',
    href: forumHref('/t/oxford-welsh-learners-group/675'),
  },
  {
    when: 'Saturdays · noon UK',
    title: 'The Saturday at Noon Zoom',
    body: 'Drop-in conversation practice on Zoom every Saturday. Mixed levels welcome. Organised on the forum, run by learners.',
    href: forumHref('/t/the-saturday-at-noon-uk-time-zoom/30130'),
  },
  {
    when: '1–8 August 2026',
    title: 'Eisteddfod Genedlaethol – Sir Benfro',
    body: 'The big one. The thread gets used for lift-sharing, accommodation, who’s going to which gig, where to meet up.',
    href: forumHref('/t/eisteddfod-genedlaethol-1-8-august-2026-sir-benfro/41198'),
  },
  {
    when: 'Touring',
    title: 'Llon‐dain – the London Meet Up',
    body: '381 replies, moves around the city. The South East has its own thread (CACEN); so does pretty much every English region with Welsh learners.',
    href: forumHref('/t/llon-dain-the-touring-london-meet-up/17465'),
  },
];

function CommunityMeetups() {
  return (
    <section style={cStyles.meetWrap} data-screen-label="Meetups">
      <div style={cStyles.meetInner}>
        <div style={cStyles.sectKicker}>Real people. Real places.</div>
        <h2 style={cStyles.sectH2}>Languages are the best excuse to make friends.</h2>
        <p style={cStyles.meetLede}>
          The forum is where the off-screen happens: bootcamps, pub nights, Eisteddfod
          lift-shares, Tuesday evenings in Oxford. Here are a few of the standing events.
        </p>

        <ul style={cStyles.meetList} className="c-meet-list">
          {MEETUPS.map(m => (
            <li key={m.title} style={cStyles.meetRow}>
              <div style={cStyles.meetWhen}>{m.when}</div>
              <div style={cStyles.meetBody}>
                <Link to={m.href} style={cStyles.meetTitle}>{m.title}</Link>
                <p style={cStyles.meetP}>{m.body}</p>
              </div>
              <Link to={m.href} style={cStyles.meetArrow} aria-label={`Open ${m.title}`}>→</Link>
            </li>
          ))}
        </ul>

        <div style={cStyles.slackCard}>
          <div style={cStyles.slackKicker}>And there's a Slack, too</div>
          <h3 style={cStyles.slackH3}>Welsh Speaking Practice on Slack</h3>
          <p style={cStyles.slackP}>
            For when "tomorrow on the forum" isn't soon enough. WSP is a free, ongoing
            Slack space for daily voice and text practice – open to learners at any level.
            The forum thread has the joining link and house rules.
          </p>
          <Link to={forumHref('/t/slack-welsh-speaking-practice-wsp-event-listings/25974')} style={cStyles.slackLink}>
            How to join WSP →
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------- Final CTA ---------- */

function CommunityCta() {
  return (
    <section style={cStyles.ctaWrap} data-screen-label="CTA">
      <div style={cStyles.ctaInner}>
        <div style={cStyles.ctaKicker}>Come and say hello</div>
        <h2 style={cStyles.ctaH2}>
          <span style={cStyles.ctaGreet}>Croeso.</span>
          <span style={cStyles.ctaGreet}>Bienvenidos.</span>
          <span style={cStyles.ctaGreet}>Benvenuti.</span>
          <span style={cStyles.ctaGreet}>Bienvenue.</span>
          <span style={cStyles.ctaGreet}>Fáilte.</span>
        </h2>
        <p style={cStyles.ctaP}>
          The forum's been here since before most language apps existed. There's room
          for you whether you're on lesson 1, lesson 100, or you only ever signed up to
          read along.
        </p>
        <div style={cStyles.ctaActions}>
          <Link to={forumHref('/')} className="btn btn--onDark">Visit the forum</Link>
          <Link to={forumHref('/t/why-welsh-why-ssiw-whats-your-story/36184')} style={cStyles.ctaGhost}>
            Read "Why Welsh?" stories →
          </Link>
        </div>
      </div>
    </section>
  );
}

export { CommunityHero, CommunityCorners, CommunityLive, CommunityMeetups, CommunityCta };

const cStyles = {
  sectKicker: { display:'inline-flex', alignItems:'center', gap:8, fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:600, marginBottom:14 },
  sectH2: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(40px, 5.4vw, 64px)', lineHeight:1.05, letterSpacing:'-.018em', margin:'0 0 24px', textWrap:'balance', color:'var(--fg-1)' },

  heroWrap: { background:'var(--offWhite)', padding:'112px 32px 88px' },
  heroInner: { maxWidth:1080, margin:'0 auto' },
  heroKicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:600, marginBottom:18 },
  heroH1: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(56px, 8vw, 104px)', lineHeight:.98, letterSpacing:'-.024em', margin:'0 0 28px', textWrap:'balance', maxWidth:'18ch' },
  heroH1Em: { fontStyle:'italic', color:'var(--gold)', fontWeight:700 },
  heroLede: { fontSize:20, lineHeight:1.55, color:'var(--fg-1)', margin:'0 0 18px', maxWidth:'56ch', textWrap:'pretty' },
  heroActions: { display:'flex', alignItems:'center', gap:24, marginTop:32, flexWrap:'wrap' },
  heroSecondary: { fontSize:15, fontWeight:500, color:'var(--fg-1)', textDecoration:'none' },
  heroStats: { display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:24, marginTop:64, paddingTop:32, borderTop:'1px solid var(--border)' },
  heroStat: {},
  heroStatN: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(36px, 4vw, 52px)', lineHeight:1, letterSpacing:'-.02em', color:'var(--brandRed)', marginBottom:8 },
  heroStatL: { fontSize:14, color:'var(--grey)', lineHeight:1.4, maxWidth:'18ch' },

  cornersWrap: { background:'#fff', padding:'112px 32px 96px' },
  cornersInner: { maxWidth:1180, margin:'0 auto' },
  cornersLede: { fontSize:18, lineHeight:1.6, color:'var(--grey)', margin:'0 0 48px', maxWidth:'58ch' },
  cornersGrid: { display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:20 },
  cornerCard: {
    display:'block', textDecoration:'none', color:'var(--fg-1)',
    padding:'40px 36px 32px',
    background:'var(--offWhite)', border:'1px solid var(--border)', borderRadius:18,
    transition:'border-color 180ms, transform 180ms, box-shadow 180ms',
  },
  cornerN: { fontFamily:'var(--font-display)', fontWeight:700, fontStyle:'italic', fontSize:20, color:'var(--brandRed)', letterSpacing:'.02em', marginBottom:18 },
  cornerH3: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(24px, 2.4vw, 32px)', lineHeight:1.15, letterSpacing:'-.01em', margin:'0 0 12px', textWrap:'balance' },
  cornerP: { fontSize:16, lineHeight:1.55, color:'var(--grey)', margin:'0 0 22px', textWrap:'pretty' },
  cornerCta: { fontSize:14, fontWeight:600, color:'var(--brandRed)' },

  liveWrap: { background:'var(--brandRed)', color:'#fff', padding:'112px 32px 112px' },
  liveInner: { maxWidth:1180, margin:'0 auto' },
  liveHead: { display:'flex', alignItems:'flex-end', justifyContent:'space-between', gap:32, marginBottom:40, flexWrap:'wrap' },
  liveDot: { display:'inline-block', width:8, height:8, borderRadius:'50%', background:'var(--gold)', boxShadow:'0 0 0 4px rgba(254,201,2,.22)' },
  liveAll: { color:'#fff', textDecoration:'none', fontSize:15, fontWeight:600, paddingBottom:4, borderBottom:'1px solid rgba(255,255,255,.5)' },

  threadGrid: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:18 },
  threadCard: {
    display:'flex', flexDirection:'column', gap:14,
    padding:'28px 26px 24px',
    background:'#fff', border:'1px solid rgba(0,0,0,0)', borderRadius:14,
    textDecoration:'none', color:'var(--fg-1)',
    minHeight:200,
    transition:'transform 180ms, box-shadow 180ms',
  },
  threadTop: { display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 },
  threadCat: { fontSize:11, letterSpacing:'.1em', textTransform:'uppercase', fontWeight:600, color:'var(--brandRed)', background:'var(--pastelRed)', padding:'4px 10px', borderRadius:999 },
  threadActivity: { fontSize:12, color:'var(--grey)' },
  threadTitle: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:20, lineHeight:1.25, letterSpacing:'-.01em', margin:0, color:'var(--fg-1)', textWrap:'pretty', flex:1 },
  threadFoot: { display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginTop:'auto', paddingTop:8, borderTop:'1px solid var(--border)' },
  posters: { display:'flex', alignItems:'center' },
  posterSlot: { display:'inline-block', lineHeight:0 },
  avatar: {
    display:'inline-flex', alignItems:'center', justifyContent:'center',
    width:28, height:28, borderRadius:'50%',
    fontFamily:'var(--font-display)', fontWeight:700, fontSize:13,
    border:'2px solid #fff',
  },
  replies: { fontSize:13, color:'var(--grey)' },

  meetWrap: { background:'var(--offWhite)', padding:'112px 32px 96px' },
  meetInner: { maxWidth:1080, margin:'0 auto' },
  meetLede: { fontSize:18, lineHeight:1.6, color:'var(--grey)', margin:'0 0 40px', maxWidth:'58ch' },
  meetList: { listStyle:'none', padding:0, margin:'0 0 48px', display:'flex', flexDirection:'column' },
  meetRow: {
    display:'grid', gridTemplateColumns:'180px 1fr 32px',
    alignItems:'start', gap:32,
    padding:'28px 0',
    borderTop:'1px solid var(--border)',
  },
  meetWhen: { fontFamily:'var(--font-display)', fontStyle:'italic', fontWeight:700, fontSize:18, color:'var(--brandRed)', paddingTop:2 },
  meetBody: {},
  meetTitle: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(22px, 2vw, 26px)', lineHeight:1.2, color:'var(--fg-1)', textDecoration:'none', display:'inline-block', marginBottom:6, letterSpacing:'-.005em' },
  meetP: { fontSize:16, lineHeight:1.55, color:'var(--grey)', margin:0, maxWidth:'60ch', textWrap:'pretty' },
  meetArrow: { fontSize:24, color:'var(--brandRed)', textDecoration:'none', alignSelf:'center', justifySelf:'end' },

  slackCard: {
    padding:'40px 40px 36px',
    background:'var(--pastelRed)',
    border:'1px solid rgba(219,30,23,.18)',
    borderRadius:20,
  },
  slackKicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', fontWeight:600, color:'var(--brandRed)', marginBottom:10 },
  slackH3: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(26px, 2.4vw, 32px)', lineHeight:1.2, margin:'0 0 12px', color:'var(--fg-1)', letterSpacing:'-.005em' },
  slackP: { fontSize:17, lineHeight:1.55, color:'var(--fg-1)', margin:'0 0 18px', maxWidth:'58ch' },
  slackLink: { fontSize:15, fontWeight:600, color:'var(--brandRed)', textDecoration:'none' },

  ctaWrap: { background:'var(--brandRed)', color:'#fff', padding:'112px 32px 120px' },
  ctaInner: { maxWidth:980, margin:'0 auto', textAlign:'center' },
  ctaKicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', fontWeight:600, color:'var(--gold)', marginBottom:18 },
  ctaH2: { fontFamily:'var(--font-display)', fontWeight:700, fontStyle:'italic', fontSize:'clamp(40px, 5.6vw, 72px)', lineHeight:1.05, letterSpacing:'-.018em', margin:'0 0 24px', color:'#fff', display:'flex', flexWrap:'wrap', justifyContent:'center', gap:'8px 24px' },
  ctaGreet: { display:'inline-block' },
  ctaP: { fontSize:20, lineHeight:1.55, color:'rgba(255,255,255,.85)', margin:'0 auto 36px', maxWidth:'52ch' },
  ctaActions: { display:'inline-flex', alignItems:'center', gap:24, flexWrap:'wrap', justifyContent:'center' },
  ctaGhost: { color:'#fff', textDecoration:'none', fontSize:15, fontWeight:500, opacity:.85, paddingBottom:4, borderBottom:'1px solid rgba(255,255,255,.4)' },
};
